import { RouteProp, useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { AssessmentType, useGetAssessmentQuery } from "@axio/graphql/generated/graphql";
import { useCallback, useEffect, useMemo, useState } from "react";
import Loader from "../components/common/status/Loader/Loader";
import McqAssessment from "../containers/assessment/mcqAssessment/McqAssessment";
import RealtimeAssessment from "../containers/assessment/realtimeAssessment/RealtimeAssessment";
import TurnBasedAssessment from "../containers/assessment/turnbasedAssessment/TurnBasedAssessment";
import { useAppConfig } from "../AppConfigProvider";
import { useAuth } from "../hooks/useAuth";
import LTILayout from "../layouts/LTILayout";
import Sentry from "../utils/sentry";
import type { RootStackParamList } from "../navigation/navigation.types";

function LTIAssessmentTakingView() {
  const route = useRoute<RouteProp<RootStackParamList, "LTIAssessmentTaking">>();
  const routeParams = route.params ?? {};
  const { assessmentId, accessToken, refreshToken } = routeParams;
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { login, isAuthenticated, didCheckToken } = useAuth();
  const { hasTurnBasedAssessments } = useAppConfig() ?? {};

  const searchParams = useMemo(() => {
    if (typeof window === "undefined") {
      return new URLSearchParams();
    }
    return new URLSearchParams(window.location.search);
  }, []);

  const courseId = routeParams.courseId ?? searchParams.get("courseId") ?? "";
  const integrationId = routeParams.integrationId ?? searchParams.get("integrationId") ?? "";
  const moduleId = routeParams.moduleId ?? searchParams.get("moduleId") ?? undefined;

  const [authReady, setAuthReady] = useState(false);

  useFocusEffect(
    useCallback(() => {
      if (!accessToken || !refreshToken) return;

      let isActive = true;

      const bootstrapAuth = async () => {
        try {
          await login(accessToken, refreshToken);
        } catch (error) {
          Sentry.captureException(error);
          if (!isActive) return;
          navigation.reset({
            index: 0,
            routes: [{ name: "LTIErrorView", params: { error: "auth_failed" } }],
          });
          return;
        }

        if (!isActive) return;
        setAuthReady(true);
      };

      void bootstrapAuth();

      return () => {
        isActive = false;
      };
    }, [accessToken, login, navigation, refreshToken])
  );

  useEffect(() => {
    if (accessToken && refreshToken) return;
    if (!didCheckToken) return;

    if (isAuthenticated) {
      setAuthReady(true);
      return;
    }

    navigation.reset({
      index: 0,
      routes: [{ name: "LTIErrorView", params: { error: "auth_failed" } }],
    });
  }, [accessToken, didCheckToken, isAuthenticated, navigation, refreshToken]);

  useEffect(() => {
    if (!authReady || assessmentId) return;
    navigation.reset({
      index: 0,
      routes: [{ name: "LTIErrorView", params: { error: "invalid_launch" } }],
    });
  }, [assessmentId, authReady, navigation]);

  const {
    data: assessment,
    loading: assessmentLoading,
    error: assessmentError,
  } = useGetAssessmentQuery({
    variables: {
      assessmentId: assessmentId ?? "",
    },
    skip: !authReady || !assessmentId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!authReady || assessmentLoading || assessment === undefined) return;

    if (assessmentError || !assessment?.getAssessment?.data) {
      navigation.reset({
        index: 0,
        routes: [{ name: "LTIErrorView", params: { error: "invalid_launch" } }],
      });
    }
  }, [assessment, assessmentError, assessmentLoading, authReady, navigation]);

  const assessmentData = assessment?.getAssessment?.data;
  const assessmentType = assessmentData?.assessmentType;
  const isUnsupportedAssessmentType =
    Boolean(assessmentData) &&
    assessmentType !== AssessmentType.Mcq &&
    assessmentType !== AssessmentType.SocraticDialog;

  useEffect(() => {
    if (!isUnsupportedAssessmentType) return;

    navigation.reset({
      index: 0,
      routes: [{ name: "LTIErrorView", params: { error: "invalid_launch" } }],
    });
  }, [isUnsupportedAssessmentType, navigation]);

  const assessmentRouteParams = useMemo(
    () => ({
      assessmentId: assessmentId ?? "",
      courseId,
      integrationId,
      moduleId,
    }),
    [assessmentId, courseId, integrationId, moduleId]
  );

  if (!authReady || assessmentLoading) {
    return (
      <LTILayout>
        <Loader />
      </LTILayout>
    );
  }

  if (!assessmentData || isUnsupportedAssessmentType) {
    return null;
  }

  if (assessmentType === AssessmentType.Mcq) {
    return <McqAssessment {...assessmentRouteParams} />;
  }

  if (hasTurnBasedAssessments) {
    return <TurnBasedAssessment {...assessmentRouteParams} />;
  }

  return <RealtimeAssessment {...assessmentRouteParams} />;
}

export default LTIAssessmentTakingView;
